import type { CSSProperties } from "react";
import { supabase } from "@/lib/supabase";

// === Cosmetic flair (purely vanity — no gameplay or leaderboard-score impact) ===
//
// Players spend tokens on a name TITLE and a name COLOR, shown wherever their
// name appears (leaderboard, profile, results). This catalog is the presentation
// layer; the authoritative prices live in the `cosmetics` table in the DB and
// are read by the purchase RPC, so a tampered client can't buy at a discount.
// The ids/costs here MUST stay in sync with the seed in supabase/schema.sql.
//
// Colors are applied as INLINE STYLES rather than Tailwind classes on purpose:
// Tailwind doesn't scan lib/, so class strings here would be purged. Inline
// styles are immune to that and keep each cosmetic fully self-contained.

export type CosmeticKind = "title" | "color";
export type CosmeticTier = "common" | "rare" | "legendary";

export interface Cosmetic {
  id: string;
  kind: CosmeticKind;
  // For a title: the text shown in the flair chip. For a color: its name.
  label: string;
  cost: number;
  tier: CosmeticTier;
  // Colors only: the style applied to the player's name text.
  style?: CSSProperties;
}

const solid = (color: string): CSSProperties => ({ color });

// A clipped text-gradient that works without any global keyframes/utilities.
const gradient = (...stops: string[]): CSSProperties => ({
  backgroundImage: `linear-gradient(90deg, ${stops.join(", ")})`,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  WebkitTextFillColor: "transparent",
});

export const COSMETICS: Cosmetic[] = [
  // — Titles —
  { id: "npc", kind: "title", label: "Certified NPC", cost: 150, tier: "common" },
  { id: "intern", kind: "title", label: "The Intern", cost: 150, tier: "common" },
  { id: "mid", kind: "title", label: "Aggressively Mid", cost: 150, tier: "common" },
  { id: "menace", kind: "title", label: "Menace to Society", cost: 400, tier: "rare" },
  { id: "innocent", kind: "title", label: "Did Nothing Wrong", cost: 400, tier: "rare" },
  { id: "lore", kind: "title", label: "Lore-Accurate", cost: 400, tier: "rare" },
  { id: "finalboss", kind: "title", label: "Final Boss", cost: 900, tier: "legendary" },
  { id: "immortal", kind: "title", label: "Refuses to Die", cost: 900, tier: "legendary" },
  { id: "nerf", kind: "title", label: "Nerf Incoming", cost: 900, tier: "legendary" },

  // — Name colors —
  { id: "crimson", kind: "color", label: "Crimson", cost: 150, tier: "common", style: solid("#f87171") },
  { id: "gold", kind: "color", label: "Gold", cost: 150, tier: "common", style: solid("#fbbf24") },
  { id: "azure", kind: "color", label: "Azure", cost: 150, tier: "common", style: solid("#38bdf8") },
  { id: "inferno", kind: "color", label: "Inferno", cost: 400, tier: "rare", style: gradient("#f59e0b", "#ef4444") },
  { id: "venom", kind: "color", label: "Venom", cost: 400, tier: "rare", style: gradient("#a3e635", "#22c55e") },
  { id: "void", kind: "color", label: "Void", cost: 400, tier: "rare", style: gradient("#c084fc", "#6366f1") },
  { id: "prismatic", kind: "color", label: "Prismatic", cost: 900, tier: "legendary", style: gradient("#f0abfc", "#818cf8", "#22d3ee") },
  { id: "molten", kind: "color", label: "Molten Gold", cost: 900, tier: "legendary", style: gradient("#fde68a", "#f59e0b", "#b45309") },
];

const BY_ID: Record<string, Cosmetic> = Object.fromEntries(
  COSMETICS.map((c) => [c.id, c]),
);

export function getCosmetic(id?: string | null): Cosmetic | undefined {
  return id ? BY_ID[id] : undefined;
}

export const TITLES = COSMETICS.filter((c) => c.kind === "title");
export const COLORS = COSMETICS.filter((c) => c.kind === "color");

// The inline style for an equipped color id (or {} when none / unknown), for
// rendering a player's name. Safe to pass an unknown/legacy id.
export function cosmeticColorStyle(id?: string | null): CSSProperties {
  return getCosmetic(id)?.style ?? {};
}

// The display text for an equipped title id (or null when none / not a title).
export function cosmeticTitle(id?: string | null): string | null {
  const c = getCosmetic(id);
  return c && c.kind === "title" ? c.label : null;
}

// === Server mutations (the catalog's prices are enforced server-side) ===

// Buys a cosmetic: the RPC reads its price from the DB, deducts tokens, and
// records ownership atomically. Returns the new token balance.
export async function purchaseCosmetic(id: string): Promise<number> {
  const { data, error } = await supabase.rpc("purchase_cosmetic", { p_id: id });
  if (error) throw error;
  return data as number;
}

// Equips (or, with id = null, unequips) a cosmetic of the given kind. The RPC
// verifies ownership server-side, so you can't equip what you don't own.
export async function equipCosmetic(
  kind: CosmeticKind,
  id: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("equip_cosmetic", {
    p_kind: kind,
    p_id: id,
  });
  if (error) throw error;
}
